import { Trans } from "@lingui/react/macro";
import React, { Fragment } from "react";
import { MenuItem, Navbar, NavDropdown, Nav } from "react-bootstrap";
import { isReadOnlyAccount } from "../util/settings";
import { Link, NavLink, useMatch } from "react-router";
import { Icon } from "./Icon";
import { MenuLink } from "./MenuLink";
import { EnvironmentContext } from "../themes/environment";
import { withI18n } from "../../withI18n";

const NavBarContext = React.createContext(() => {});

// see https://github.com/ReactTraining/react-router/issues/4554#issuecomment-280954472
const HighlightedLink = ({ to, exact, query = "", children, ...props }) => {
  const match = useMatch({ path: to, end: !!exact });
  return (
    <li className={match ? "active" : ""}>
      <NavBarContext.Consumer>
        {(closeNavBar) => (
          <NavLink to={to + query} onClick={closeNavBar}>
            <Icon {...props} />
            &nbsp;
            {children}
          </NavLink>
        )}
      </NavBarContext.Consumer>
    </li>
  );
};

const ExternalLink = ({ href, children, ...props }) => (
  <li>
    <a target="_blank" rel="noopener noreferrer" href={href}>
      <Icon {...props} /> {children}
    </a>
  </li>
);

const Home = () => (
  <HighlightedLink to="/" exact glyphicon="home">
    <Trans id="nav.home">Startseite</Trans>
  </HighlightedLink>
);

const Shop = () => (
  <HighlightedLink to="/shop" exact glyphicon="shopping-cart">
    <Trans id="nav.shop">Shop</Trans>
  </HighlightedLink>
);

const ExternalShop = ({ href }) => (
  <ExternalLink href={href} exact glyphicon="shopping-cart">
    <Trans id="nav.shop">Shop</Trans>
  </ExternalLink>
);

const Forum = () => (
  <ExternalLink href="https://forum.hivewatch.net" glyphicon="comment">
    <Trans id="nav.forum">Forum</Trans>
  </ExternalLink>
);

const Map = () => (
  <HighlightedLink to={"/community/global/map"} fontAwesome="map">
    <Trans id="nav.map">Karte</Trans>
  </HighlightedLink>
);

const Dashboard = () => (
  <HighlightedLink to="/dashboard" glyphicon="dashboard">
    <Trans id="nav.dashboard">Dashboard</Trans>
  </HighlightedLink>
);

const Timeline = ({ query }) => (
  <HighlightedLink to="/timeline" query={query} fontAwesome="line-chart">
    <Trans id="nav.scalecomparison">Waagenvergleich</Trans>
  </HighlightedLink>
);

const Settings = () => (
  <HighlightedLink to="/settings" glyphicon="cog">
    <Trans id="nav.settings">Einstellungen</Trans>
  </HighlightedLink>
);

const Support = withI18n()(({ i18n, href }) => (
  <ExternalLink href={href} glyphicon="question-sign">
    <Trans id="nav.support">Support</Trans>
  </ExternalLink>
));

const Logout = () => (
  <li>
    <Link to="/logout">
      <Icon glyphicon="log-out" /> <Trans id="nav.logout">Logout</Trans>
    </Link>
  </li>
);

const LanguagePicker = ({ title, setLocale }) => (
  <NavDropdown title={title.toUpperCase()} id="language-picker">
    <MenuItem onClick={() => setLocale("de")}>Deutsch</MenuItem>
    <MenuItem onClick={() => setLocale("en")}>English</MenuItem>
    <MenuItem onClick={() => setLocale("fr")}>Français</MenuItem>
    <MenuItem onClick={() => setLocale("es")}>Español</MenuItem>
  </NavDropdown>
);

const MyMenuItem = ({ href, children, icon, ...props }) => (
  <NavBarContext.Consumer>
    {(closeNavBar) => (
      // MenuLink also closes the parent <details> on click — the plain Link
      // left the admin menu hanging open after navigating (NEXT-56).
      <MenuLink to={href} onClick={closeNavBar}>
        <Icon name={icon} {...props} /> {children}
      </MenuLink>
    )}
  </NavBarContext.Consumer>
);
const Admin = () => (
  <NavDropdown id="nav-admin" title={<Icon glyphicon="king" />}>
    <MyMenuItem href="/a/users" fontAwesome="users">
      <Trans id="settings.settingsview.usermanagement">
        Benutzerverwaltung
      </Trans>
    </MyMenuItem>
    <MyMenuItem href="/a/notifications" fontAwesome="bell">
      <Trans id="settings.settingsview.notifications">Benachrichtigungen</Trans>
    </MyMenuItem>
    <MyMenuItem href="/a/controllers" glyphicon="tasks">
      <Trans id="nav.controllers">Funkstationen</Trans>
    </MyMenuItem>
    <MyMenuItem href="/a/controllers/create" glyphicon="plus">
      <Trans id="settings.settingsview.registercontrollers">
        Funkstationen Registrieren
      </Trans>
    </MyMenuItem>
    <MyMenuItem href="/a/jobs" glyphicon="briefcase">
      <Trans id="settings.settingsview.jobs">Jobs</Trans>
    </MyMenuItem>
    <MyMenuItem href="/a/firmware" glyphicon="file">
      <Trans id="settings.settingsview.firmware">Firmware</Trans>
    </MyMenuItem>
    <MyMenuItem href="/a/variables" fontAwesome="cogs">
      <Trans id="nav.admin.variables">Variablen</Trans>
    </MyMenuItem>
    <MyMenuItem href="/shop/admin" fontAwesome="credit-card">
      <Trans id="nav.orders">Bestellungen</Trans>
    </MyMenuItem>
    <MyMenuItem href="/a/regions" fontAwesome="globe">
      <Trans id="nav.regions">Regionen</Trans>
    </MyMenuItem>
    <MyMenuItem href="/a/map/global" fontAwesome="map">
      <Trans id="nav.admin.map">Karte</Trans>
    </MyMenuItem>
    <MyMenuItem href="/m/affiliateNotification" glyphicon="bell">
      <Trans id="nav.affiliateNotification">Kunden Benachrichtigen</Trans>
    </MyMenuItem>
  </NavDropdown>
);

const Manager = () => (
  <NavDropdown
    id="nav-admin"
    title={
      <Trans id="nav.admin">
        <Icon glyphicon="king" /> Admin
      </Trans>
    }
  >
    <MyMenuItem href="/m/controllers" glyphicon="tasks">
      <Trans id="nav.controllers">Funkstationen</Trans>
    </MyMenuItem>
    <MyMenuItem href="/m/users" fontAwesome="users">
      <Trans id="settings.settingsview.usermanagement">
        Benutzerverwaltung
      </Trans>
    </MyMenuItem>
  </NavDropdown>
);

class NavigationBar extends React.Component {
  constructor(props) {
    super(props);
    // this is used to determine which navbar to display
    this.props.restoreSession().catch((e) => {
      // ignore
    });

    this.containerRef = React.createRef();

    this.state = {
      expanded: false,
    };
  }

  componentDidMount() {
    const container = this.containerRef.current;
    if (!container) return;

    // The Navbar inside is position:fixed, so the wrapper has 0 in-flow
    // height. Observe the actual .navbar element instead.
    const el = container.querySelector(".navbar") ?? container;

    const publish = (height) => {
      // When the navbar is in normal flow (e.g. on very small screens where
      // _general.scss forces nav to position:relative) it already pushes
      // following content down — paddingTop on consumers would double-pad.
      const position = window.getComputedStyle(el).position;
      const overlays = position === "fixed" || position === "absolute";
      document.documentElement.style.setProperty(
        "--navbar-height",
        overlays ? `${height}px` : "0px",
      );
    };

    publish(el.getBoundingClientRect().height);

    if (typeof ResizeObserver === "undefined") return;

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // border-box height, not entry.contentRect (content-box): consumers
        // offset by the navbar's full visual height including its padding.
        publish(entry.target.getBoundingClientRect().height);
      }
    });
    this.resizeObserver.observe(el);
  }

  componentWillUnmount() {
    this.resizeObserver?.disconnect();
    document.documentElement.style.removeProperty("--navbar-height");
  }

  render() {
    const loggedIn = this.props.user.email;
    const admin =
      this.props.user.admin && !isReadOnlyAccount(this.props.user.email);
    const manager = this.props.user.manager;

    return (
      <EnvironmentContext.Consumer>
        {({
          logoSmall,
          navColor,
          devMode,
          shopFeatures,
          supportUrl,
          externalShopUrl,
        }) => {
          return (
            <NavBarContext.Provider
              value={() => this.setState({ expanded: false })}
            >
              <div id="navigationbar-container" ref={this.containerRef}>
                <Navbar
                  fluid
                  fixedTop
                  style={{ backgroundColor: navColor }}
                  expanded={this.state.expanded}
                  onToggle={() =>
                    this.setState({ expanded: !this.state.expanded })
                  }
                >
                  <Navbar.Header>
                    <Navbar.Brand>
                      <Link to="/">
                        <img
                          className="hw-large"
                          alt="HiveWatch Logo"
                          src={logoSmall}
                        />
                      </Link>
                    </Navbar.Brand>
                    <Navbar.Toggle />
                  </Navbar.Header>
                  <Navbar.Collapse>
                    <Nav pullRight>
                      {loggedIn ? (
                        <Fragment>
                          <Dashboard />
                          <Timeline query={this.props.timelineQuery} />
                          {shopFeatures && <Shop />}
                          {externalShopUrl && (
                            <ExternalShop href={externalShopUrl} />
                          )}
                          <Forum />
                          {<Map />}
                          {supportUrl && <Support href={supportUrl} />}
                          <Settings />
                          {manager && !admin && <Manager />}
                          {admin && <Admin />}
                          <Logout />
                        </Fragment>
                      ) : (
                        <Fragment>
                          <Home />
                          {shopFeatures && <Shop />}
                          {externalShopUrl && (
                            <ExternalShop href={externalShopUrl} />
                          )}
                          <Forum />
                        </Fragment>
                      )}
                      <LanguagePicker
                        title={this.props.locale}
                        setLocale={this.props.setLocale}
                      />
                    </Nav>
                  </Navbar.Collapse>
                </Navbar>
              </div>
            </NavBarContext.Provider>
          );
        }}
      </EnvironmentContext.Consumer>
    );
  }
}

export default withI18n()(NavigationBar);
